import { Button } from "./button";
import { Card, CardContent } from "./card";
import { AlertCircle, ShieldAlert, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

interface ErrorScreenProps {
  /** Error object from API call */
  error?: unknown;
  /** Custom error message to display */
  message?: string;
  /** Custom title for the error */
  title?: string;
  /** Show back button with custom link */
  backLink?: string;
  /** Custom back button label */
  backLabel?: string;
  /** Show retry button */
  showRetry?: boolean;
  /** Custom retry handler */
  onRetry?: () => void;
  /** Full screen mode (default: true) */
  fullScreen?: boolean;
}

/**
 * Reusable error screen component that handles different error types
 * Automatically detects 404 Access Denied errors and displays appropriate UI
 */
export const ErrorScreen = ({
  error,
  message,
  title,
  backLink,
  backLabel = "Go Back",
  showRetry = true,
  onRetry,
  fullScreen = true,
}: ErrorScreenProps) => {
  // Extract error details
  const getErrorStatus = (err: unknown): number | undefined => {
    if (err && typeof err === 'object' && 'response' in err) {
      const response = (err as any).response;
      return response?.status;
    }
    return undefined;
  };

  const getErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      const response = (err as any).response;
      const data = response?.data;
      return data?.message || data?.error || 'An error occurred';
    }
    if (err && typeof err === 'object' && 'message' in err) {
      return (err as any).message;
    }
    return 'An unknown error occurred';
  };

  const errorStatus = error ? getErrorStatus(error) : undefined;
  const errorMessage = message || (error ? getErrorMessage(error) : 'An error occurred');
  const is404AccessDenied = errorStatus === 404 && errorMessage.toLowerCase().includes("access denied");

  const containerClass = fullScreen 
    ? "min-h-screen bg-background p-4 sm:p-6 lg:p-8" 
    : "p-4 sm:p-6 lg:p-8";

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className={containerClass}>
      <div className="max-w-4xl mx-auto">
        <Card className={`shadow-sm border ${is404AccessDenied ? 'border-orange-300 bg-orange-50/50' : 'border-destructive/30 bg-card'}`}>
          <CardContent className="p-12 text-center">
            {is404AccessDenied ? (
              <>
                <ShieldAlert className="h-16 w-16 text-orange-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {title || "Access Denied"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  You don't have permission to access this resource. Please contact your administrator if you believe this is an error.
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                  {backLink && (
                    <Link to={backLink}>
                      <Button variant="outline" className="shadow-sm">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {backLabel}
                      </Button>
                    </Link>
                  )}
                  {showRetry && (
                    <Button
                      onClick={handleRetry}
                      variant="outline"
                      className="text-orange-600 border-orange-600 hover:bg-orange-50 shadow-sm"
                    >
                      Try Again
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {title || "Something Went Wrong"}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm">
                  {errorMessage}
                </p>
                <div className="flex gap-3 justify-center flex-wrap">
                  {backLink && (
                    <Link to={backLink}>
                      <Button variant="outline" className="shadow-sm">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        {backLabel}
                      </Button>
                    </Link>
                  )}
                  {showRetry && (
                    <Button
                      onClick={handleRetry}
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50 shadow-sm"
                    >
                      Try Again
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
